import { useState, type FormEvent } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { api } from "../../../lib/api.js";
import { useAuth } from "../../../lib/auth-context.js";

export const Route = createFileRoute("/_app/settings/workspaces")({
  component: WorkspacesSettings,
});

function WorkspacesSettings() {
  const { workspace, workspaces, refresh, switchWorkspace } = useAuth();
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [creatingError, setCreatingError] = useState<string | null>(null);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [logoutAllDone, setLogoutAllDone] = useState(false);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    setCreatingError(null);
    setBusy(true);
    try {
      const res = await api.post<{ workspace: { id: string } }>(
        "/workspaces",
        { name: newName.trim() },
      );
      await switchWorkspace(res.data.workspace.id);
      setNewName("");
      setCreating(false);
    } catch (err: unknown) {
      type ApiErr = { response?: { data?: { errors?: Array<{ message: string }>; message?: string } } };
      const data = (err as ApiErr).response?.data;
      setCreatingError(data?.errors?.[0]?.message ?? data?.message ?? "Failed");
    } finally {
      setBusy(false);
    }
  }

  async function handleRename(id: string) {
    if (!renameDraft.trim()) return;
    setBusy(true);
    try {
      await api.patch(`/workspaces/${id}`, { name: renameDraft.trim() });
      setRenamingId(null);
      setRenameDraft("");
      await refresh();
    } catch {
      // surface inline next iteration; keep simple for 1F
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(id: string) {
    if (!confirm("Delete this workspace? All data inside it will be removed.")) {
      return;
    }
    setBusy(true);
    try {
      await api.delete(`/workspaces/${id}`);
      await refresh();
    } catch (err: unknown) {
      type ApiErr = { response?: { data?: { message?: string } } };
      const msg =
        (err as ApiErr).response?.data?.message ?? "Could not delete";
      alert(msg);
    } finally {
      setBusy(false);
    }
  }

  async function handleLogoutAll() {
    if (!confirm("Sign out of every browser, including this one?")) return;
    try {
      await api.post("/auth/logout-all");
    } catch {
      /* still clear */
    }
    setLogoutAllDone(true);
    setTimeout(() => {
      window.location.href = "/login";
    }, 600);
  }

  return (
    <div>
      <h1 style={{ fontSize: 40, fontWeight: 900, letterSpacing: "-0.03em", margin: "0 0 24px" }}>
        Workspaces
      </h1>

      <div style={{ display: "grid", gap: 16 }}>
        {workspaces.map((w) => (
          <div key={w.id} className="brut-card" style={{ padding: 20 }}>
            {renamingId === w.id ? (
              <form
                onSubmit={(e) => { e.preventDefault(); void handleRename(w.id); }}
                style={{ display: "flex", gap: 8, alignItems: "center" }}
              >
                <input
                  className="brut-input"
                  value={renameDraft}
                  onChange={(e) => setRenameDraft(e.target.value)}
                  autoFocus
                  required
                  maxLength={120}
                />
                <button type="submit" className="brut-button" disabled={busy}>
                  Save
                </button>
                <button
                  type="button"
                  className="brut-button is-secondary"
                  onClick={() => setRenamingId(null)}
                >
                  Cancel
                </button>
              </form>
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                  flexWrap: "wrap",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <strong style={{ fontSize: 18 }}>{w.name}</strong>
                  {w.isPersonal && (
                    <span className="brut-tag is-personal">Personal</span>
                  )}
                  {w.id === workspace?.id && <span className="brut-tag">Active</span>}
                  <span style={{ color: "var(--muted)", fontSize: 13 }}>
                    {w.role} · {w.memberCount} member{w.memberCount === 1 ? "" : "s"}
                  </span>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  {w.id !== workspace?.id && (
                    <button
                      type="button"
                      className="brut-button is-secondary"
                      onClick={() => switchWorkspace(w.id)}
                      style={{ padding: "6px 12px", fontSize: 13 }}
                    >
                      Switch
                    </button>
                  )}
                  <button
                    type="button"
                    className="brut-button is-secondary"
                    onClick={() => {
                      setRenamingId(w.id);
                      setRenameDraft(w.name);
                    }}
                    style={{ padding: "6px 12px", fontSize: 13 }}
                  >
                    Rename
                  </button>
                  <button
                    type="button"
                    className="brut-button is-danger"
                    onClick={() => void handleDelete(w.id)}
                    disabled={busy}
                    style={{ padding: "6px 12px", fontSize: 13 }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div style={{ marginTop: 32 }}>
        {creating ? (
          <form onSubmit={handleCreate} className="brut-card" style={{ padding: 20 }}>
            <label className="brut-label" htmlFor="ws-name">New workspace name</label>
            <input
              id="ws-name"
              className="brut-input"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              required
              maxLength={120}
              autoFocus
            />
            {creatingError && <div className="brut-error">{creatingError}</div>}
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button type="submit" className="brut-button" disabled={busy}>
                {busy ? "Creating…" : "Create"}
              </button>
              <button
                type="button"
                className="brut-button is-secondary"
                onClick={() => { setCreating(false); setNewName(""); setCreatingError(null); }}
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <button type="button" className="brut-button" onClick={() => setCreating(true)}>
            + New workspace
          </button>
        )}
      </div>

      <hr style={{ margin: "48px 0", border: 0, borderTop: "var(--border)" }} />

      <h2 style={{ fontSize: 24, fontWeight: 900, margin: "0 0 12px" }}>Sessions</h2>
      <p style={{ color: "var(--muted)", marginTop: 0 }}>
        Sign out of every browser this account is signed in on. Useful if a
        device was lost.
      </p>
      {logoutAllDone ? (
        <p>Signing out…</p>
      ) : (
        <button
          type="button"
          className="brut-button is-danger"
          onClick={() => void handleLogoutAll()}
        >
          Log out everywhere
        </button>
      )}
    </div>
  );
}
